'use client';

import { useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Breadcrumb from '@/components/common/Breadcrumb';
import { useAuth } from '@/contexts/AuthContext';
import { fetchUserOrders } from '@/lib/services/orderService';
import { toast } from 'sonner';

export default function MyOrdersClient() {
  const { user } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState('');

  // Return Modal States
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedOrderForReturn, setSelectedOrderForReturn] = useState(null);

  const [returnReason, setReturnReason] = useState('Wrong item');
  const [returnType, setReturnType] = useState('refund');
  const [returnComment, setReturnComment] = useState('');
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  //  CRITICAL FIX: Track if orders already loaded
  const loadedRef = useRef(false);
  const loadingRef = useRef(false);

  //  EMERGENCY TIMEOUT: Force stop after 5 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        //console.log('⏰ TIMEOUT: Force stopping spinner');
        setLoading(false);
        if (!orders.length && !error) {
          setError('Request timed out. Please refresh.');
        }
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, [loading, orders.length, error]);

  useEffect(() => {
    const loadOrders = async () => {
      //  Prevent duplicate calls
      if (loadingRef.current) {
       // console.log('⏭️ Already loading, skipping...');
        return;
      }

      // User not logged in
      if (!user?.id) {
        //console.log('👤 No user logged in');
        setOrders([]);
        setError('');
        setLoading(false);
        loadedRef.current = false;
        return;
      }

      // Already loaded for this user
      if (loadedRef.current && orders.length > 0) {
        //console.log('✅ Orders already loaded, skipping...');
        return;
      }

      try {
       // console.log('📦 Loading orders for:', user.id);
        loadingRef.current = true;
        setLoading(true);
        setError('');

        const data = await fetchUserOrders(user.id);
        //console.log('✅ Orders loaded:', data?.length || 0);
       // console.log('📋 Orders data:', data);

        setOrders(Array.isArray(data) ? data : []);
        loadedRef.current = true;
      } catch (err) {
        //console.error(' MyOrders error:', err);
        setError(err?.message || 'Failed to load orders');
        setOrders([]);
        loadedRef.current = false;
      } finally {
       // console.log(' LOADING FINISHED');
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadOrders();
  }, [user?.id]); // Only user.id dependency

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'Invalid Date';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    const num = Number(amount || 0);
    if (Number.isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  const getReturnDaysLeft = (order, windowDays = 15) => {
    if (!order?.updated_at) return null;

    const deliveredDate = new Date(order.updated_at);
    if (isNaN(deliveredDate.getTime())) return null;

    const now = new Date();
    const diffMs = now.getTime() - deliveredDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    const daysLeft = Math.ceil(windowDays - diffDays);
    return daysLeft > 0 ? daysLeft : 0;
  };

  const getStatusBadge = (status) => {
    const s = String(status || 'pending').toLowerCase();

    const map = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      shipped: 'bg-purple-100 text-purple-800 border-purple-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
    };

    return map[s] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getReturnStatusLabel = (returnStatus) => {
    const s = String(returnStatus || '').toLowerCase();

    const map = {
      requested: 'Return Requested',
      approved: 'Return Approved',
      rejected: 'Return Rejected',
      picked: 'Return Picked',
      refunded: 'Refunded',
      completed: 'Return Completed',
    };

    return map[s] || `Return: ${returnStatus}`;
  };

  const isReturnEligible = (order) => {
    const orderStatus = String(order?.status || '').toLowerCase();
    const returnStatus = String(
      order?.return_status || order?.returnStatus || ''
    ).toLowerCase();

    if (orderStatus !== 'delivered') return false;
    if (returnStatus && returnStatus !== 'null') return false;

    const daysLeft = getReturnDaysLeft(order);
    if (!daysLeft || daysLeft <= 0) return false;

    return true;
  };

  const submitReturnRequest = async () => {
    try {
      if (!user?.id) {
        toast.error('Please login first');
        return;
      }

      if (!selectedOrderForReturn?.id) {
        toast.error('Order not found');
        return;
      }

      setReturnSubmitting(true);

      const res = await fetch('/api/returns/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: selectedOrderForReturn.id,
          userId: user.id,
          reason: returnReason,
          returnType,
          comment: returnComment,
        }),
      });

      const result = await res.json();

      if (!res.ok || !result?.success) {
        toast.error(result?.error || 'Failed to submit return request');
        return;
      }

      toast.success('Return request submitted');

      setOrders((prev) =>
        prev.map((o) =>
          String(o?.id) === String(selectedOrderForReturn?.id)
            ? { ...o, return_status: 'requested' }
            : o
        )
      );

      setShowReturnModal(false);
      setSelectedOrderForReturn(null);
      setReturnComment('');
      setReturnReason('Wrong item');
      setReturnType('refund');
    } catch (err) {
      console.error('Return submit error:', err);
      toast.error(err?.message || 'Something went wrong');
    } finally {
      setReturnSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-20 lg:pt-24">
        <Breadcrumb />

        <main className="w-full px-4 py-3 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {/* Top Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
              <div>
                <h1 className="text-2xl md:text-3xl font-extrabold text-foreground">
                  My Orders
                </h1>
                <p className="text-muted-foreground mt-1">
                  View your purchases, payment type and order status.
                </p>
              </div>

              <Link
                href="/product-catalog"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth text-sm font-semibold"
              >
                Continue Shopping →
              </Link>
            </div>

            {/* States */}
            {!user?.id ? (
              <div className="bg-card border border-border rounded-2xl p-10 text-center">
                <p className="text-xl font-bold text-foreground">
                  Please login first
                </p>
                <p className="text-muted-foreground mt-2">
                  Login to view your orders.
                </p>

                <Link
                  href="/login"
                  className="inline-flex mt-5 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth text-sm font-semibold"
                >
                  Go to Login
                </Link>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center min-h-[40vh] p-8">
                <div className="text-center space-y-3">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
                  <div>
                    <p className="text-muted-foreground">Loading your orders...</p>
                    <p className="text-xs text-muted-foreground/60">Please wait</p>
                  </div>
                </div>
              </div>
            ) : error ? (
              <div className="bg-card border border-border rounded-2xl p-6">
                <p className="text-foreground font-semibold mb-2">
                  Something went wrong
                </p>
                <p className="text-muted-foreground mb-4">{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  Refresh Page
                </button>
              </div>
            ) : orders?.length === 0 ? (
              <div className="bg-card border border-border rounded-2xl p-10 text-center">
                <p className="text-xl font-bold text-foreground">
                  No orders yet
                </p>
                <p className="text-muted-foreground mt-2">
                  You haven't placed any orders.
                </p>

                <Link
                  href="/product-catalog"
                  className="inline-flex mt-5 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth text-sm font-semibold"
                >
                  Browse Products
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {orders?.map((order) => {
                  const total = Number(order?.total || 0);
                  const orderNumber =
                    order?.order_id || order?.orderId || order?.id;
                  const createdAt = order?.created_at || order?.createdAt;

                  const returnStatus =
                    order?.return_status || order?.returnStatus || '';

                  return (
                    <div
                      key={order?.id}
                      className="bg-card border border-border rounded-2xl p-5 hover:shadow-lg transition-smooth"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">
                            Order ID
                          </p>
                          <p className="font-bold text-foreground truncate">
                            #{orderNumber}
                          </p>

                          <p className="text-sm text-muted-foreground mt-2">
                            Placed on{' '}
                            <span className="font-medium">
                              {formatDate(createdAt)}
                            </span>
                          </p>

                          {returnStatus ? (
                            <p className="mt-2 text-xs font-semibold text-muted-foreground">
                              {getReturnStatusLabel(returnStatus)}
                            </p>
                          ) : null}
                        </div>

                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(
                            order?.status
                          )}`}
                        >
                          {order?.status || 'pending'}
                        </span>
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Payment
                          </p>
                          <p className="text-sm font-semibold text-foreground">
                            {order?.payment_method ||
                              order?.paymentMethod ||
                              'N/A'}
                          </p>
                        </div>

                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Total</p>
                          <p className="text-lg font-extrabold text-foreground">
                            {formatCurrency(total)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-5 flex items-center justify-between gap-3">
                        <Link
                          href={`/orders/${order.id}`}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-border bg-background hover:bg-muted transition-smooth text-sm font-semibold"
                        >
                          View Details
                        </Link>

                        {isReturnEligible(order) ? (
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs text-muted-foreground">
                              {getReturnDaysLeft(order)} days left to return
                            </span>

                            <button
                              onClick={() => {
                                setSelectedOrderForReturn(order);
                                setShowReturnModal(true);
                              }}
                              className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-smooth text-sm font-semibold"
                            >
                              Return
                            </button>
                          </div>
                        ) : returnStatus ? (
                          <span className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-muted text-foreground text-sm font-semibold">
                            {getReturnStatusLabel(returnStatus)}
                          </span>
                        ) : (
                          <Link
                            href={`/orders/${order.id}#need-help`}
                            className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-muted hover:bg-muted/70 transition-smooth text-sm font-semibold"
                          >
                            Help
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Return Modal - SAME AS BEFORE */}
            {showReturnModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 pt-20">
                <div className="w-full max-w-lg bg-card border border-border rounded-2xl p-5 md:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-foreground">
                      Return Request
                    </h3>
                    <button
                      onClick={() => {
                        setShowReturnModal(false);
                        setSelectedOrderForReturn(null);
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Thin Warning Line */}
                    <div className="mb-3 border-l-4 border-amber-400 bg-amber-50 px-3 py-2 rounded text-xs text-amber-800">
                      ⚠ Buyers pay for return shipping. Shipping fees are non-refundable.
                    </div>

                  <div className="space-y-4">
                    <div className="p-3 rounded-xl border border-border bg-background">
                      <p className="text-xs text-muted-foreground">Order</p>
                      <p className="text-sm font-semibold text-foreground truncate">
                        #{selectedOrderForReturn?.order_id || selectedOrderForReturn?.id}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        Reason
                      </label>
                      <select
                        value={returnReason}
                        onChange={(e) => setReturnReason(e.target.value)}
                        className="w-full border border-border rounded-lg px-3 py-2 bg-background"
                      >
                        <option>Wrong item</option>
                        <option>Damaged product</option>
                        <option>Not as described</option>
                        <option>Quality issue</option>
                        <option>Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        Return Type
                      </label>
                      <select
                        value={returnType}
                        onChange={(e) => setReturnType(e.target.value)}
                        className="w-full border border-border rounded-lg px-3 py-2 bg-background"
                      >
                        <option value="refund">Refund</option>
                        <option value="replacement">Replacement</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">
                        Comment (optional)
                      </label>
                      <textarea
                        value={returnComment}
                        onChange={(e) => setReturnComment(e.target.value)}
                        placeholder="Write your message..."
                        className="w-full border border-border rounded-lg px-3 py-2 bg-background min-h-[90px]"
                      />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-0.5">
                      <button
                        onClick={() => {
                          setShowReturnModal(false);
                          setSelectedOrderForReturn(null);
                        }}
                        className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-smooth text-sm font-semibold"
                      >
                        Cancel
                      </button>

                      <button
                        onClick={submitReturnRequest}
                        disabled={returnSubmitting}
                        className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth text-sm font-semibold disabled:opacity-60"
                      >
                        {returnSubmitting ? 'Submitting...' : 'Submit Return'}
                      </button>
                    </div>
                      <p className="text-[10px] text-muted-foreground text-center mt-1">Please read the return policy carefully before submitting your request.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
